import { Divider, Flex, HelpHover, SimpleGrid, Switch, Text } from 'components'
import { startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Action, ILayout, LayoutType } from 'types'
import {
  LayoutWithKeyPath,
  allLayoutTypes,
  containerLayoutTypes,
  createLayout,
  iconByLayoutType,
} from '../../../utils/templateBuilderUtils'
import { useTemplate } from '../TemplateProvider'
import { useTemplateLayout } from './TemplateLayoutProvider'

interface Props {
  parentKeyPath?: string
  onSelect?: (layout?: LayoutWithKeyPath) => void
}

export default function LayoutSelect({ parentKeyPath, onSelect }: Props) {
  const { template, updateTemplate } = useTemplate()
  const { selectedLayout } = useTemplateLayout()

  const [isAction, setIsAction] = useState(false)

  const parentKey = Number(parentKeyPath?.split('/').pop())
  const parentLayout = template?.layoutMap?.[parentKey]
  const isContainer = 'layouts' in (parentLayout ?? {})

  useEffect(() => {
    setIsAction(!isContainer)
  }, [isContainer])

  if (!parentLayout) return null

  const containerTypes = allLayoutTypes.filter(type => containerLayoutTypes.includes(type))
  const contentTypes = allLayoutTypes.filter(type => !containerLayoutTypes.includes(type))
  const hasAction = selectedLayout?.action != null

  function handleSelect(layoutType: LayoutType) {
    if (!template?.layoutMap || !selectedLayout) return onSelect?.()

    const newLayoutKey = getNextAvailableKey(template.layoutMap)
    const newLayout = createLayout(layoutType)
    const keyPath = parentKeyPath + '/' + newLayoutKey

    if (isAction) addActionToLayout(newLayoutKey, newLayout)
    else addChildToParent(newLayoutKey, newLayout)
    onSelect?.({ keyPath, ...newLayout })
  }

  function getNextAvailableKey(layoutMap: Record<number, ILayout>): number {
    const existingKeys = Object.keys(layoutMap).map(Number)
    return existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1
  }

  function addChildToParent(newLayoutKey: number, newLayout: ILayout) {
    if (!template?.layoutMap) return

    if (parentLayout && 'layouts' in parentLayout) {
      const updatedParentLayout = {
        ...parentLayout,
        layouts: [...parentLayout.layouts, { key: newLayoutKey }],
      }

      const updatedLayoutMap = {
        ...template.layoutMap,
        [parentKey]: updatedParentLayout,
        [newLayoutKey]: newLayout,
      }

      updateTemplate({ layoutMap: updatedLayoutMap })
    }
  }

  function addActionToLayout(newLayoutKey: number, newLayout: ILayout) {
    if (!template?.layoutMap || !selectedLayout) return

    const action: Action = { type: 'dialog', title: { en: 'Action' }, layoutKey: newLayoutKey }

    const updatedLayoutMap = {
      ...template.layoutMap,
      [parentKey]: { ...selectedLayout, action },
      [newLayoutKey]: newLayout,
    }

    updateTemplate({ layoutMap: updatedLayoutMap })
  }

  const renderLayoutItem = (layoutType: LayoutType) => {
    const Icon = iconByLayoutType[layoutType]
    return (
      <Flex
        key={layoutType}
        flexDirection="column"
        alignItems="center"
        gap={2}
        p={2}
        borderRadius="md"
        borderWidth="1px"
        cursor="pointer"
        opacity={1}
        _hover={{ bg: 'gray.50' }}
        onClick={() => handleSelect(layoutType)}
      >
        <Icon size="sm" />
        <Text textStyle="bodyMedium" textAlign="center">
          {startCase(layoutType)}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap={4}>
      <Flex alignItems="center" gap={2}>
        <Switch
          label="Dialog Action"
          checked={isAction}
          disabled={!isContainer || hasAction}
          onChange={setIsAction}
        />
        <HelpHover text="When enabled, the new layout will be added as a dialog action to the selected layout instead of as a child layout." />
      </Flex>

      <Text textStyle="h5">Containers</Text>
      <SimpleGrid gap={2} columns={4}>
        {containerTypes.map(type => renderLayoutItem(type))}
      </SimpleGrid>

      <Divider />

      <Text textStyle="h5">Content</Text>
      <SimpleGrid gap={2} columns={4}>
        {contentTypes.map(type => renderLayoutItem(type))}
      </SimpleGrid>
    </Flex>
  )
}
